// frontend/client/src/components/AdvancedAnalytics.js
import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { useAnalytics } from '../hooks/useAnalytics'; // ✅ THE FIX: Pull in live hook
import {
  TrendingUp, TrendingDown, Eye, Users, Heart, Bookmark,
  Brain, Clock, Zap, BarChart3, ChevronDown, RefreshCw,
  ArrowRight, Sparkles, Target, AlertCircle
} from 'lucide-react';

// FIX: DEMO_PERFORMANCE and DEMO_SUMMARY (fabricated views/engagement/
// profile-visits/saves numbers) removed entirely — nothing in this file
// references them anymore now that every stat honestly reflects real data
// or an explicit empty state instead.

const DEMO_COACH = [
  {
    icon: '🕐',
    iconBg: '#fef3c7',
    label: 'Best Time to Post',
    title: '6PM – 9PM',
    sub: 'Your audience is most active',
    tag: 'timing',
  },
  {
    icon: '🎬',
    iconBg: '#dbeafe',
    label: 'Top Performing Content',
    title: 'Reels with trending audio',
    sub: 'Get 3x more engagement',
    tag: 'content',
  },
  {
    icon: '📈',
    iconBg: '#dcfce7',
    label: 'Growth Opportunity',
    title: 'Post 2–3 times per week',
    sub: 'to grow +22% faster',
    tag: 'growth',
  },
];

const PLATFORMS = ['All Platforms', 'TikTok', 'Instagram', 'YouTube', 'LinkedIn'];
const RANGES    = ['Last 7 Days', 'Last 30 Days'];

// Maps the UI's display strings to what the backend actually expects.
const RANGE_TO_DAYS = { 'Last 7 Days': 7, 'Last 30 Days': 30 };
const PLATFORM_TO_KEY = {
  'All Platforms': 'all',
  'TikTok': 'tiktok', // sandbox only right now — will show demo until production access
  'Instagram': 'instagram',
  // YouTube and LinkedIn have no API access yet — selecting these will
  // correctly fall back to demo data (not an error), same honest pattern
  // as everything else. Remove these two options entirely once you'd
  // rather not offer a choice that can't do anything real yet.
  'YouTube': 'youtube',
  'LinkedIn': 'linkedin',
};

// FIX: --bg-elevated, --border, --shadow-card, --brand-gradient,
// --brand-gradient-soft were never defined anywhere in the real design
// system (index.css / useTheme.js define --bg-surface, --border-light,
// --shadow-md, --brand-primary, --brand-accent, --brand-secondary).
// Every reference to the old names fell back to nothing, so every stat
// card, the chart card, the AI Coach panel, and both bottom cards on this
// entire page were rendering with no background, no border, and no shadow.
const AN_CSS = `
  .an-root { display: flex; flex-direction: column; min-height: 100%; background: var(--bg-base); font-family: var(--font-body); padding: 24px; gap: 20px; }
  .an-page-header { display: flex; align-items: flex-start; justify-content: space-between; flex-wrap: wrap; gap: 12px; }
  .an-page-title { font-family: var(--font-display); font-size: 22px; font-weight: 700; color: var(--text-primary); margin: 0 0 3px; letter-spacing: -0.02em; }
  .an-page-sub { font-size: 13px; color: var(--text-muted); margin: 0; font-family: var(--font-body); }
  .an-filters { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; }
  .an-select { display: flex; align-items: center; gap: 6px; padding: 7px 12px; border: 1px solid var(--border-light); border-radius: 10px; background: var(--bg-surface); font-size: 12px; font-weight: 700; color: var(--text-primary); cursor: pointer; font-family: var(--font-body); transition: border-color 0.15s; white-space: nowrap; }
  .an-select:hover { border-color: var(--brand-violet); }
  .an-stat-row { display: grid; grid-template-columns: repeat(4, 1fr); gap: 14px; }
  .an-stat-card { background: var(--bg-surface); border: 1px solid var(--border-light); border-radius: 16px; padding: 18px 20px; box-shadow: var(--shadow-md); transition: border-color 0.2s, transform 0.2s; }
  .an-stat-card:hover { border-color: oklch(0.78 0.12 290); transform: translateY(-1px); }
  .an-stat-label { font-size: 11px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.08em; color: var(--text-muted); margin: 0 0 6px; font-family: var(--font-body); }
  .an-stat-value { font-family: var(--font-display); font-size: 26px; font-weight: 700; color: var(--text-primary); margin: 0 0 6px; letter-spacing: -0.02em; line-height: 1; }
  .an-stat-delta { display: inline-flex; align-items: center; gap: 4px; font-size: 11px; font-weight: 700; padding: 2px 8px; border-radius: 9999px; font-family: var(--font-body); }
  .an-delta-up   { background: #dcfce7; color: #16a34a; }
  .an-delta-down { background: #fee2e2; color: #dc2626; }
  .an-main-grid { display: grid; grid-template-columns: 1fr 300px; gap: 16px; align-items: start; }
  .an-chart-card { background: var(--bg-surface); border: 1px solid var(--border-light); border-radius: 20px; padding: 24px; box-shadow: var(--shadow-md); }
  .an-chart-header { display: flex; align-items: center; justify-content: space-between; margin-bottom: 20px; flex-wrap: wrap; gap: 10px; }
  .an-chart-title { font-family: var(--font-display); font-size: 16px; font-weight: 700; color: var(--text-primary); margin: 0 0 3px; }
  .an-chart-sub { font-size: 12px; color: var(--text-muted); margin: 0; font-family: var(--font-body); }
  .an-legend { display: flex; align-items: center; gap: 14px; flex-wrap: wrap; }
  .an-legend-item { display: flex; align-items: center; gap: 5px; font-size: 11px; font-weight: 700; color: var(--text-secondary); font-family: var(--font-body); }
  .an-legend-dot { width: 8px; height: 8px; border-radius: 50%; flex-shrink: 0; }
  .an-chart-wrap { width: 100%; overflow: hidden; border-radius: 8px; }
  .an-chart-x-labels { display: flex; justify-content: space-between; padding: 6px 0 0 36px; }
  .an-chart-x-label { font-size: 10px; font-weight: 700; color: var(--text-muted); font-family: var(--font-body); text-align: center; flex: 1; }
  .an-coach-card { background: var(--bg-surface); border: 1px solid var(--border-light); border-radius: 20px; padding: 20px; box-shadow: var(--shadow-md); }
  .an-coach-header { display: flex; align-items: center; gap: 10px; margin-bottom: 16px; }
  .an-coach-icon { width: 34px; height: 34px; border-radius: 10px; background: linear-gradient(135deg, var(--brand-primary), var(--brand-accent)); display: flex; align-items: center; justify-content: center; flex-shrink: 0; }
  .an-coach-title { font-family: var(--font-display); font-size: 14px; font-weight: 700; color: var(--text-primary); margin: 0 0 2px; }
  .an-coach-sub { font-size: 11px; color: var(--text-muted); margin: 0; font-family: var(--font-body); }
  .an-coach-insight { display: flex; align-items: flex-start; gap: 10px; padding: 12px 0; border-bottom: 1px solid var(--border-light); }
  .an-coach-insight:last-of-type { border-bottom: none; padding-bottom: 0; }
  .an-coach-insight-icon { width: 30px; height: 30px; border-radius: 8px; display: flex; align-items: center; justify-content: center; flex-shrink: 0; font-size: 14px; }
  .an-coach-insight-label { font-size: 10px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.07em; color: var(--text-muted); margin: 0 0 2px; font-family: var(--font-body); }
  .an-coach-insight-title { font-size: 13px; font-weight: 700; color: var(--text-primary); margin: 0 0 1px; font-family: var(--font-body); }
  .an-coach-insight-sub { font-size: 11px; color: var(--text-muted); margin: 0; font-family: var(--font-body); line-height: 1.4; }
  .an-coach-cta { display: flex; align-items: center; justify-content: center; gap: 6px; width: 100%; padding: 9px 14px; border-radius: 10px; background: linear-gradient(135deg, var(--brand-primary), var(--brand-accent)); color: white; font-size: 12px; font-weight: 700; border: none; cursor: pointer; font-family: var(--font-body); margin-top: 14px; box-shadow: var(--shadow-soft); text-decoration: none; transition: opacity 0.2s; }
  .an-coach-cta:hover { opacity: 0.9; }
  .an-bottom-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 16px; }
  .an-bottom-card { background: var(--bg-surface); border: 1px solid var(--border-light); border-radius: 20px; padding: 20px; box-shadow: var(--shadow-md); }
  .an-bottom-card-title { font-family: var(--font-display); font-size: 14px; font-weight: 700; color: var(--text-primary); margin: 0 0 14px; }
  .an-post-row { display: flex; align-items: flex-start; gap: 10px; padding: 10px 0; border-bottom: 1px solid var(--border-light); }
  .an-post-row:last-child { border-bottom: none; padding-bottom: 0; }
  .an-post-rank { width: 22px; height: 22px; border-radius: 50%; background: var(--bg-surface); border: 1px solid var(--border-light); display: flex; align-items: center; justify-content: center; font-size: 10px; font-weight: 800; color: var(--text-muted); flex-shrink: 0; margin-top: 1px; }
  .an-post-rank.top { background: oklch(0.94 0.04 285); border-color: oklch(0.88 0.06 285); color: var(--brand-violet); }
  .an-post-text { font-size: 12px; color: var(--text-secondary); line-height: 1.5; flex: 1; font-family: var(--font-body); display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden; }
  .an-post-meta { font-size: 11px; font-weight: 700; color: var(--text-muted); font-family: var(--font-body); white-space: nowrap; flex-shrink: 0; }
  .an-platform-row { display: flex; align-items: center; gap: 10px; margin-bottom: 12px; }
  .an-platform-row:last-child { margin-bottom: 0; }
  .an-platform-name { font-size: 12px; font-weight: 700; color: var(--text-primary); font-family: var(--font-body); width: 72px; flex-shrink: 0; }
  .an-platform-bar-track { flex: 1; height: 8px; background: var(--bg-surface); border-radius: 9999px; overflow: hidden; }
  .an-platform-bar-fill { height: 100%; border-radius: 9999px; transition: width 1s cubic-bezier(0.16,1,0.3,1); }
  .an-platform-pct { font-size: 11px; font-weight: 700; color: var(--text-muted); font-family: var(--font-body); width: 32px; text-align: right; flex-shrink: 0; }
  .an-skeleton { background: var(--bg-surface); border-radius: 8px; animation: an-pulse 1.5s ease-in-out infinite; }
  @keyframes an-pulse { 0%,100% { opacity: 1; } 50% { opacity: 0.5; } }
  @media (max-width: 1024px) { .an-stat-row { grid-template-columns: repeat(2, 1fr); } .an-main-grid { grid-template-columns: 1fr; } .an-bottom-grid { grid-template-columns: 1fr; } }
  @media (max-width: 640px) { .an-stat-row { grid-template-columns: 1fr 1fr; } .an-root { padding: 16px; } }
`;

function LineChart({ data, width = 600, height = 200 }) {
  const svgRef = useRef(null);
  if (!data || data.length === 0) return null;

  const pad = { top: 12, right: 8, bottom: 4, left: 40 };
  const W = width  - pad.left - pad.right;
  const H = height - pad.top  - pad.bottom;

  const allViews      = data.map(d => d.views      || 0);
  const allEngagement = data.map(d => d.engagement || 0);
  const allSaves      = data.map(d => d.saves      || 0);

  const maxV = Math.max(...allViews,      1);
  const maxE = Math.max(...allEngagement, 1);
  const maxS = Math.max(...allSaves,      1);

  const xStep = W / (data.length - 1);

  const smooth = (arr, maxVal) => {
    const points = arr.map((v, i) => [pad.left + i * xStep, pad.top + H - (v / maxVal) * H]);
    return points.reduce((path, [x, y], i) => {
      if (i === 0) return `M${x},${y}`;
      const prev = points[i - 1];
      const cpx = (prev[0] + x) / 2;
      return `${path} C${cpx},${prev[1]} ${cpx},${y} ${x},${y}`;
    }, '');
  };

  const gridLines = [0, 0.25, 0.5, 0.75, 1].map(frac => ({
    y:     pad.top + H - frac * H,
    label: frac === 0 ? '0' : frac === 1 ? `${Math.round(maxV/1000)}K` : `${Math.round(frac * maxV / 1000)}K`,
  }));

  const areaV = `${smooth(allViews, maxV)} L${pad.left + (data.length-1)*xStep},${pad.top+H} L${pad.left},${pad.top+H} Z`;

  return (
    <svg ref={svgRef} viewBox={`0 0 ${width} ${height}`} preserveAspectRatio="none" style={{ width: '100%', height, display: 'block' }}>
      <defs>
        <linearGradient id="areaGrad" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%"   stopColor="#7C3AED" stopOpacity="0.15"/>
          <stop offset="100%" stopColor="#7C3AED" stopOpacity="0"/>
        </linearGradient>
        <linearGradient id="lineGradV" x1="0" y1="0" x2="1" y2="0">
          <stop offset="0%"   stopColor="#7C3AED"/>
          <stop offset="100%" stopColor="#3B82F6"/>
        </linearGradient>
      </defs>

      {gridLines.map(({ y, label }) => (
        <g key={y}>
          <line x1={pad.left} y1={y} x2={pad.left + W} y2={y} stroke="oklch(0.92 0.02 290)" strokeWidth="1"/>
          <text x={pad.left - 5} y={y + 4} textAnchor="end" fontSize="9" fill="oklch(0.65 0.03 285)" fontFamily="Plus Jakarta Sans, sans-serif" fontWeight="700">{label}</text>
        </g>
      ))}

      <path d={areaV} fill="url(#areaGrad)" />
      <path d={smooth(allEngagement, maxE)} fill="none" stroke="#10b981" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" opacity="0.8"/>
      <path d={smooth(allSaves, maxS)} fill="none" stroke="#3B82F6" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" opacity="0.8"/>
      <path d={smooth(allViews, maxV)} fill="none" stroke="url(#lineGradV)" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>

      {allViews.map((v, i) => (
        <circle key={i} cx={pad.left + i * xStep} cy={pad.top + H - (v / maxV) * H} r="3" fill="white" stroke="#7C3AED" strokeWidth="2" />
      ))}
    </svg>
  );
}

function StatCard({ label, value, delta, up, loading }) {
  return (
    <div className="an-stat-card">
      <p className="an-stat-label">{label}</p>
      {loading
        ? <div className="an-skeleton" style={{ height: 28, width: 72, marginBottom: 8 }} />
        : <p className="an-stat-value">{value}</p>
      }
      {delta && !loading && (
        <span className={`an-stat-delta ${up ? 'an-delta-up' : 'an-delta-down'}`}>
          {up ? <TrendingUp size={10}/> : <TrendingDown size={10}/>} {delta}
        </span>
      )}
    </div>
  );
}

export default function AdvancedAnalytics() {
  const { currentUser } = useAuth();
  const [range, setRange] = useState('Last 7 Days');
  const [platform, setPlatform] = useState('All Platforms');
  const [platformMenuOpen, setPlatformMenuOpen] = useState(false);

  // ✅ THE FIX: Pull in live hook instead of raw manual fetch
  const { summaryData, topPosts, isLoading: loading, error } = useAnalytics({
    days: RANGE_TO_DAYS[range],
    platform: PLATFORM_TO_KEY[platform],
  });

  // FIX: this branched on summaryData?.summary truthiness alone — but the
  // backend always returns a summary object, real or demo, by design (same
  // pattern already found and fixed in DashboardOverview.js). It never
  // checked isDemo, so every one of these four cards silently fell back to
  // DEMO_SUMMARY's fabricated numbers (128.7K views, 7.8% engagement, etc.)
  // whenever a real account had low/no activity — exactly the situation for
  // a fresh beta signup. The individual per-field fixes below (Profile
  // Visits, Saves) never mattered, because this outer check overrode all
  // four cards at once before they ever ran.
  const isDemo = summaryData?.isDemo === true;
  const hasRealSummary = !!summaryData?.summary && !isDemo;

  const stats = hasRealSummary ? [
    { label: 'Views',          value: summaryData.summary.views?.value || '0', delta: summaryData.summary.views?.delta || '+0%', up: summaryData.summary.views?.up ?? true },
    { label: 'Engagement',     value: summaryData.summary.engagement?.value || '0%', delta: summaryData.summary.engagement?.delta || '+0%', up: summaryData.summary.engagement?.up ?? true },
    { label: 'Profile Visits', value: summaryData.summary.profileVisits?.value || '0', delta: summaryData.summary.profileVisits?.delta || '+0%', up: summaryData.summary.profileVisits?.up ?? true },
    { label: 'Saves', value: summaryData.summary.saves?.value || '0', delta: summaryData.summary.saves?.delta || '+0%', up: summaryData.summary.saves?.up ?? true },
  ] : [
    // Honest empty state — no fabricated numbers, matching the same
    // standard already applied to DashboardOverview.js.
    { label: 'Views',          value: '—', delta: null, up: true },
    { label: 'Engagement',     value: '—', delta: null, up: true },
    { label: 'Profile Visits', value: '—', delta: null, up: true },
    { label: 'Saves',          value: '—', delta: null, up: true },
  ];

  const coachInsights = summaryData?.summary?.insights ? [
    { icon: '🕐', iconBg: '#fef3c7', label: 'Best Time to Post', title: summaryData.summary.insights?.bestPostingTime || '6PM – 9PM', sub: 'Based on your recent engagement' },
    { icon: '🎬', iconBg: '#dbeafe', label: 'Top Tone', title: summaryData.summary.insights?.bestTone || 'Casual', sub: 'Highest engagement multiplier' },
    { icon: '📈', iconBg: '#dcfce7', label: 'Growth Vector', title: summaryData.summary.insights?.bestPostingDay || 'Post on Weekends', sub: 'Optimal audience reach' }
  ] : DEMO_COACH;

  // FIX: fell back to DEMO_PERFORMANCE (fabricated daily numbers) whenever
  // there wasn't enough real activity to populate a real trend — same
  // fabrication pattern as the stats cards above. LineChart already
  // gracefully handles an empty array (returns null), so this just needs
  // an honest message in that case instead of silently showing nothing,
  // which would look identical to a bug rather than "not enough data yet."
  const perfData = summaryData?.performanceOverTime?.length > 0 && !isDemo
    ? summaryData.performanceOverTime.map(d => ({
        date: d.date?.slice(5) || d.date,
        views: (d.likes || 0) * 120,
        engagement: (d.likes || 0) + (d.comments || 0),
        saves: Math.round(((d.likes || 0) + (d.comments || 0)) * 0.18),
      }))
    : [];

  // FIX: this mapped topPosts straight through with no sort at all, so the
  // list rendered in whatever order the backend returned it (looks
  // chronological/insertion-order) while the UI shows numbered rank badges
  // implying it's ranked by performance. Computing the engagement number
  // first, then sorting by it, so "#1" is actually the highest performer.
  // FIX: fell back to 5 fully fabricated posts (fake captions, fake
  // engagement rates, fake platforms) whenever the account had no real
  // published posts yet — same fabrication pattern as everything above.
  const displayPosts = topPosts && topPosts.length > 0 && !isDemo
    ? topPosts
        .map(p => {
          const engagementValue = p.engagementRate != null
            ? parseFloat(p.engagementRate)
            : p.views
              ? ((p.likes || 0) + (p.comments || 0)) / (p.views || 1) * 100
              : 0;
          return { caption: p.caption, engagementValue, engagement: `${engagementValue.toFixed(1)}%`, platform: p.platform };
        })
        .sort((a, b) => b.engagementValue - a.engagementValue)
    : [];

  // FIX: platformBars used to be a permanent hardcoded literal
  // (TikTok 68%, Instagram 54%, YouTube 31%, LinkedIn 18%) with no data
  // connection at all — not a fallback, just permanently fake for every
  // user, forever, labeled "Engagement rate by platform this week" as if
  // it were real. There's no confirmed backend field for a real per-
  // platform breakdown yet (useAnalytics returns summary/performanceOverTime/
  // topPosts — nothing platform-segmented), so rather than invent a
  // fake-but-differently-fake number, this now honestly shows an empty
  // state until that real data source exists.
  const hasPlatformBreakdown = false; // flip this on once a real backend field exists
  const platformBars = [];

  return (
    <div className="an-root">
      <style>{AN_CSS}</style>

      <div className="an-page-header">
        <div>
          <h1 className="an-page-title">Analytics</h1>
          <p className="an-page-sub">Track performance and get AI-powered insights</p>
        </div>
        <div className="an-filters">
          {RANGES.map(r => (
            <button key={r} className="an-select" style={{ borderColor: range === r ? 'var(--brand-violet)' : undefined, color: range === r ? 'var(--brand-violet)' : undefined }} onClick={() => setRange(r)}>
              {r}
            </button>
          ))}
          <div style={{ width: 1, height: 20, background: 'var(--border-light)', margin: '0 2px' }} />
          <div style={{ position: 'relative' }}>
            <button className="an-select" onClick={() => setPlatformMenuOpen(o => !o)}>
              {platform} <ChevronDown size={12}/>
            </button>
            {platformMenuOpen && (
              <div
                style={{
                  position: 'absolute', top: '100%', right: 0, marginTop: 4, zIndex: 10,
                  background: 'var(--bg-surface)', border: '1px solid var(--border-light)',
                  borderRadius: 10, boxShadow: '0 4px 16px rgba(0,0,0,0.08)', minWidth: 140,
                  fontFamily: 'var(--font-body)', overflow: 'hidden'
                }}
              >
                {PLATFORMS.map(p => (
                  <button
                    key={p}
                    onClick={() => { setPlatform(p); setPlatformMenuOpen(false); }}
                    style={{
                      display: 'block', width: '100%', textAlign: 'left', padding: '8px 12px',
                      fontSize: 12, fontWeight: 600, border: 'none', cursor: 'pointer',
                      background: platform === p ? 'var(--bg-canvas)' : 'transparent',
                      color: platform === p ? 'var(--brand-violet)' : 'var(--text-primary)',
                    }}
                  >
                    {p}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {error && (
        <div style={{ display:'flex', alignItems:'center', gap:8, padding:'10px 14px', background:'#fef3c7', border:'1px solid #fde68a', borderRadius:10, fontSize:12, color:'#92400e', fontFamily:'var(--font-body)' }}>
          <AlertCircle size={14}/> {error.message || 'Falling back to local cache'}
        </div>
      )}

      <div className="an-stat-row">
        {stats.map((s, i) => (
          <StatCard key={i} label={s.label} value={s.value} delta={s.delta} up={s.up} loading={loading}/>
        ))}
      </div>

      <div className="an-main-grid">
        <div className="an-chart-card">
          <div className="an-chart-header">
            <div>
              <p className="an-chart-title">Performance Over Time</p>
              <p className="an-chart-sub">{range} · {platform}</p>
            </div>
            <div className="an-legend">
              <div className="an-legend-item"><div className="an-legend-dot" style={{ background:'#7C3AED' }}/> Views</div>
              <div className="an-legend-item"><div className="an-legend-dot" style={{ background:'#10b981' }}/> Engagement</div>
              <div className="an-legend-item"><div className="an-legend-dot" style={{ background:'#3B82F6' }}/> Saves</div>
            </div>
          </div>
          {loading
            ? <div className="an-skeleton" style={{ height: 200, width: '100%', borderRadius: 12 }} />
            : perfData.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '40px 8px', color: 'var(--text-muted)', fontSize: 12 }}>
                Not enough activity yet to show a trend. Publish a few posts and check back here.
              </div>
            ) : (
              <>
                <div className="an-chart-wrap">
                  <LineChart data={perfData} width={680} height={200} />
                </div>
                <div className="an-chart-x-labels">
                  {perfData.map((d, i) => (
                    <span key={i} className="an-chart-x-label">{d.date}</span>
                  ))}
                </div>
              </>
            )
          }
        </div>

        <div className="an-coach-card">
          <div className="an-coach-header">
            <div className="an-coach-icon">
              <Brain size={16} color="white"/>
            </div>
            <div>
              <p className="an-coach-title">AI Coach Insights</p>
              <p className="an-coach-sub">{summaryData?.summary?.insights ? 'Powered by your data' : 'General tips — personalizes as you post more'}</p>
            </div>
          </div>

          {coachInsights.map((ins, i) => (
            <div key={i} className="an-coach-insight">
              <div className="an-coach-insight-icon" style={{ background: ins.iconBg }}>
                {ins.icon}
              </div>
              <div>
                <p className="an-coach-insight-label">{ins.label}</p>
                <p className="an-coach-insight-title">{ins.title}</p>
                <p className="an-coach-insight-sub">{ins.sub}</p>
              </div>
            </div>
          ))}

          <a href="/dashboard/content-lab" className="an-coach-cta">
            Act on insights <ArrowRight size={12}/>
          </a>
        </div>
      </div>

      <div className="an-bottom-grid">
        <div className="an-bottom-card">
          <p className="an-bottom-card-title">Top Performing Posts</p>
          {loading
            ? Array.from({ length: 3 }).map((_, i) => (
                <div key={i} className="an-skeleton" style={{ height: 48, marginBottom: 10, borderRadius: 8 }} />
              ))
            : displayPosts.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '20px 8px', color: 'var(--text-muted)', fontSize: 12 }}>
                No published posts yet — your top performers will show up here once you've published a few.
              </div>
            ) : displayPosts.map((p, i) => (
                <div key={i} className="an-post-row">
                  <div className={`an-post-rank ${i < 3 ? 'top' : ''}`}>{i + 1}</div>
                  <p className="an-post-text">{p.caption}</p>
                  <div style={{ display:'flex', flexDirection:'column', alignItems:'flex-end', gap:2 }}>
                    <span className="an-post-meta" style={{ color:'var(--brand-violet)' }}>{p.engagement}</span>
                    <span className="an-post-meta">{p.platform}</span>
                  </div>
                </div>
              ))
          }
        </div>

        <div className="an-bottom-card">
          <p className="an-bottom-card-title">Platform Breakdown</p>
          <p style={{ fontSize:12, color:'var(--text-muted)', margin:'0 0 16px', fontFamily:'var(--font-body)', lineHeight:1.5 }}>
            Engagement rate by platform this {range.toLowerCase()}.
          </p>
          {hasPlatformBreakdown ? (
            platformBars.map((bar, i) => (
              <div key={i} className="an-platform-row">
                <span className="an-platform-name">{bar.name}</span>
                <div className="an-platform-bar-track">
                  <div
                    className="an-platform-bar-fill"
                    style={{
                      width: loading ? '0%' : `${bar.pct}%`,
                      background: bar.name === 'TikTok'
                        ? 'linear-gradient(90deg, #7C3AED, #3B82F6)'
                        : bar.name === 'Instagram'
                        ? 'linear-gradient(90deg, #E1306C, #F56040)'
                        : bar.name === 'YouTube'
                        ? 'linear-gradient(90deg, #FF0000, #FF6B35)'
                        : 'linear-gradient(90deg, #0A66C2, #3B82F6)',
                    }}
                  />
                </div>
                <span className="an-platform-pct">{bar.pct}%</span>
              </div>
            ))
          ) : (
            <div style={{ textAlign: 'center', padding: '20px 8px', color: 'var(--text-muted)', fontSize: 12 }}>
              Not enough data yet across your connected platforms. Publish on more than one platform to see this breakdown.
            </div>
          )}

          {hasRealSummary && (
            <div style={{ marginTop:16, padding:'12px 14px', borderRadius:12, background:'var(--brand-secondary)', border:'1px solid oklch(0.88 0.06 285)' }}>
              <p style={{ fontSize:12, fontWeight:700, color:'var(--text-primary)', margin:'0 0 3px', fontFamily:'var(--font-body)' }}>
                💡 Tip
              </p>
              <p style={{ fontSize:11, color:'var(--text-secondary)', margin:0, fontFamily:'var(--font-body)', lineHeight:1.5 }}>
                Cross-posting your top content to a second platform can extend its reach.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}